import React, { useEffect, useState } from 'react'
import { Message } from 'rsuite'
import CustomIcons from '../../../ui_reusable/CustomIcons'

import { GhostIconButtonRed } from '../../../ui_reusable/styles/Button.styles'
import { StandardToggleButton } from '../../../ui_reusable/styles/ToggleButton.styles'
import {
  WarningSection,
  OptionsSection,
  TwoColumnMainContainer, // Update import name
  Column,
  TwoColumnContainer,
  ControlColumn,
  ContentColumn,
} from '../../../ui_reusable/styles/Deactivate.styles'
import {
  HeaderLargeImportant,
  TextMediumMaxi600Style,
  HeaderMedium600Style,
  TextSmall500FadedStyle,
} from '../../../../styles/Text.styles'
import Modal, { ModalDefaultButtonFooter } from '../../../ui_reusable/Modal'
import useStandardToaster from '../../../ui_reusable/StandardToaster'
import { useNavigate } from 'react-router-dom'
import { useClientProjectContext } from '../../../../context/ClientProjectContext'
import { useProjectContext } from '../../../../context/ProjectContext'

const ProjectDeactivate = ({ project, setRefresh }) => {
  const [isActive, setIsActive] = useState(true) // This will come from API
  const [showDeactivateModal, setShowDeactivateModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showDeleteUnderstandModal, setShowDeleteUnderstandModal] =
    useState(false)
  const { pushToast } = useStandardToaster()
  const navigate = useNavigate()
  const { refreshClientProjects } = useClientProjectContext()
  const { selectedProject, setSelectedProject } = useProjectContext()
  const clientIsActive = project.client?.is_active !== false

  useEffect(() => {
    setIsActive(project.status === 'active' ? true : false)
  }, [project])

  const updateProjectStatus = async (status) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/projects/${project.id}`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status }),
        },
      )

      if (response.ok) {
        setIsActive(status === 'active' ? true : false)
        setRefresh(new Date().getTime())
        if (Number(selectedProject?.id) === Number(project.id)) {
          setSelectedProject({ ...selectedProject, status })
        }
        await refreshClientProjects()
        pushToast(
          <Message type="success">
            {status === 'active'
              ? 'Project is activated'
              : 'Project is deactivated'}
          </Message>,
          { placement: 'topCenter', duration: 5000 },
        )
      } else {
        throw new Error('Failed to update client status')
      }
    } catch (error) {
      pushToast(
        <Message type="error">An error occurred, try again later.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    }
  }

  const handleActiveToggle = async () => {
    if (!clientIsActive && !isActive) {
      return
    }
    if (isActive) {
      setShowDeactivateModal(true)
    } else {
      await updateProjectStatus('active')
    }
  }

  const handleDeleteClick = () => {
    setShowDeleteUnderstandModal(true)
  }

  const handleUnderstandClick = () => {
    setShowDeleteUnderstandModal(false)
    setShowDeleteModal(true)
  }

  const handleDeleteProject = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/projects/${project.id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        },
      )

      if (response.ok) {
        setShowDeleteModal(false)
        pushToast(<Message type="success">Project is deleted</Message>, {
          placement: 'topCenter',
          duration: 5000,
        })
        await refreshClientProjects()
        navigate('/projects')
      } else {
        throw new Error('Failed to delete project')
      }
    } catch (error) {
      pushToast(
        <Message type="error">An error occurred, try again later.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    }
  }

  return (
    <>
      <WarningSection>
        <h2 css={HeaderLargeImportant}>DANGER ZONE</h2>
        <span css={TextMediumMaxi600Style}>
          Please be very careful and very sure before making changes here.
        </span>
      </WarningSection>

      <OptionsSection>
        <TwoColumnMainContainer>
          {/* Active Status Column */}
          <Column>
            <TwoColumnContainer>
              <ControlColumn>
                <StandardToggleButton
                  checked={isActive}
                  onChange={handleActiveToggle}
                  disabled={!clientIsActive && !isActive}
                  data-testid="deactivate-project"
                />
              </ControlColumn>
              <ContentColumn>
                <h4
                  css={HeaderMedium600Style}
                  style={{ color: isActive ? '#c5841c' : '#A0A0A0' }}
                >
                  Active status
                </h4>
                <span
                  css={TextSmall500FadedStyle}
                  style={{ color: isActive ? '#c5841c' : '#A0A0A0' }}
                >
                  {clientIsActive
                    ? 'Click to deactivate project from Citrus.'
                    : 'Reactivate the client before activating this project.'}
                  <br />
                  {clientIsActive
                    ? 'This can be reverted later.'
                    : 'The client is currently inactive.'}
                </span>
              </ContentColumn>
            </TwoColumnContainer>
          </Column>

          {/* Delete Column */}
          <Column>
            <TwoColumnContainer>
              <ControlColumn>
                <GhostIconButtonRed onClick={handleDeleteClick}>
                  <CustomIcons.TrashIcon size="xs" color="#ff0000" />
                </GhostIconButtonRed>
              </ControlColumn>
              <ContentColumn>
                <h4 css={HeaderMedium600Style} style={{ color: '#b71c1c' }}>
                  Delete project
                </h4>
                <span css={TextSmall500FadedStyle} style={{ color: '#b71c1c' }}>
                  Click to delete project from Citrus. This action cannot be
                  undone.
                </span>
              </ContentColumn>
            </TwoColumnContainer>
          </Column>
        </TwoColumnMainContainer>
      </OptionsSection>

      {/* Deactivate Modal */}
      <Modal
        open={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
      >
        <Modal.Header onClose={() => setShowDeactivateModal(false)}>
          <Modal.Title>Deactivate Project?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to deactivate this project? The project with all
          its users will lose access to all models unless the project is
          activated again.
        </Modal.Body>
        <Modal.Footer>
          {ModalDefaultButtonFooter({
            confirmText: 'Deactivate',
            cancelText: 'Cancel',
            confirmColor: '#f5a623',
            onConfirm: async () => {
              await updateProjectStatus('inactive')
              setShowDeactivateModal(false)
            },
            onCancel: () => setShowDeactivateModal(false),
          })}
        </Modal.Footer>
      </Modal>

      {/* Delete Understanding Modal */}
      <Modal
        open={showDeleteUnderstandModal}
        onClose={() => setShowDeleteUnderstandModal(false)}
      >
        <Modal.Header onClose={() => setShowDeleteUnderstandModal(false)}>
          <Modal.Title>Do you understand?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Hold on. You are about to delete a project from Citrus. Do you
          understand that this action cannot be undone?
        </Modal.Body>
        <Modal.Footer>
          {ModalDefaultButtonFooter({
            confirmText: 'I understand',
            cancelText: 'Take me back',
            confirmColor: '#b71c1c',
            onConfirm: handleUnderstandClick,
            onCancel: () => setShowDeleteUnderstandModal(false),
            flip: true,
          })}
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <Modal.Header onClose={() => setShowDeleteModal(false)}>
          <Modal.Title style={{ color: '#b71c1c' }}>
            Delete Project?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            Do you want to delete the project? The project with all its users
            will lose access to all models. Individual users will permanently
            lose access to all logs, history, and datasets. Are you sure about
            this?
          </div>
        </Modal.Body>
        <Modal.Footer>
          {ModalDefaultButtonFooter({
            confirmText: 'Delete project',
            cancelText: 'Take me back',
            confirmColor: '#b71c1c',
            onConfirm: () => handleDeleteProject(),
            onCancel: () => setShowDeleteModal(false),
          })}
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default ProjectDeactivate
